import { uploadService } from '../upload/model/upload-service'
import { addUploadTask, updateUploadProgress, removeUploadTask } from '@/shared/lib/asset-upload-store'
import { requestAddMaterial } from '../api/asset-api'
import { triggerAssetRefresh } from './asset-library-store'
import { getAssetTypeFromMime, getImageDimensions, getVideoDimensions, getAudioDuration } from '../lib/asset-helper'
import type { AssetType } from './asset-types'
import { getFlowPermission } from '@/shared/config/flow-permission-store'
import { openMemberUpgradeDialog } from '@/shared/lib/member-upgrade-store'
import { IMAGE_MAX_DIMENSION } from '@/shared/config/file-restrictions'
import { checkSpaceEnough } from '@/shared/config/space-store'

export interface AssetUploadOptions {
  projectId: string
  nodeId?: string
  /** 上传进度回调（在 store 更新之后调用） */
  onProgress?: (taskId: string, percent: number) => void
  /** 中止信号 */
  abortSignal?: AbortSignal
  /** 自定义素材名，默认取 file.name 去扩展名 */
  materialName?: string
}

export interface AssetUploadResult {
  taskId: string
  url: string
  name: string
  type: AssetType
  size: number
  width: number
  height: number
  duration: number
  ext: string
  mimeType: string
  nodeId?: string
  materialId: string
}

function generateTaskId(): string {
  return Date.now() + Math.random().toString(36).substring(2)
}

/**
 * 上传文件并自动完成：
 * 1. 添加到 assetUploadStore
 * 2. 执行 OSS 上传（带进度更新）
 * 3. 读取文件尺寸/时长
 * 4. 调用 requestAddMaterial 同步到服务端素材库
 * 5. 成功后清理 store 并刷新素材库
 * 6. 失败后标记 store 状态为 failed
 *
 * 调用方只需关心业务逻辑（如更新 nodeData），不再需要管理上传生命周期。
 */
export async function uploadAsset(
  file: File,
  options: AssetUploadOptions,
): Promise<AssetUploadResult> {
  if (getFlowPermission().isReadonly) {
    throw new Error('只读模式，无法进行该操作')
  }

  // 1. 先判断空间是否足够
  if (!checkSpaceEnough()) {
    throw new Error('空间不足')
  }

  const { projectId, onProgress, abortSignal, materialName } = options

  const taskId = generateTaskId()
  const abortController = new AbortController()
  const assetType = getAssetTypeFromMime(file.type)

  // 将外部 abortSignal 链接到内部 AbortController
  if (abortSignal) {
    abortSignal.addEventListener('abort', () => abortController.abort(), { once: true })
  }

  // Step 1: 添加到上传任务 store
  addUploadTask({
    id: taskId,
    file,
    type: assetType,
    progress: 0,
    status: 'uploading',
    abortController,
    nodeId: options.nodeId,
  })

  try {
    // Step 3: 读取文件元信息
    let width = 0
    let height = 0
    let duration = 0

    if (assetType === 'image') {
      const dims = await getImageDimensions(file).catch(() => ({ width: 0, height: 0 }))
      width = dims.width
      height = dims.height
      if (width > 0 && height > 0) {
        if (width > IMAGE_MAX_DIMENSION || height > IMAGE_MAX_DIMENSION) {
          throw new Error(`图片尺寸超过限制（最大 ${IMAGE_MAX_DIMENSION}×${IMAGE_MAX_DIMENSION}px，当前 ${width}×${height}px）`)
        }
      }
    } else if (assetType === 'video') {
      const dims = await getVideoDimensions(file).catch(() => ({ width: 0, height: 0, duration: 0 }))
      width = dims.width
      height = dims.height
      duration = dims.duration
      if (width === 0 || height === 0) {
        throw new Error('无法读取视频尺寸')
      }
    } else if (assetType === 'audio') {
      width = 0
      height = 0
      duration = await getAudioDuration(file).catch(() => 0)
    }

    // Step 2: 执行物理上传
    const uploadRes = await uploadService.uploadFile(file, {
      progress: ({ percent }) => {
        updateUploadProgress(taskId, percent)
        onProgress?.(taskId, percent)
      },
      abortSignal: abortController.signal,
    })

    const uploadedUrl = uploadRes.data?.assets[0]?.url
    if (uploadRes.code !== 1 || !uploadedUrl) {
      throw new Error(uploadRes.msg || '上传失败')
    }

    // Step 4: 同步到服务端素材库
    const ext = file.name.split('.').pop() || ''
    const name = materialName ?? file.name.replace(/\.[^/.]+$/, '')

    const res = await requestAddMaterial(projectId, {
      name,
      type: assetType,
      url: uploadedUrl,
      size: file.size,
      width,
      height,
      duration,
      ext,
      node_id: options.nodeId,
      mime_type: file.type,
    })

    if (res.code !== 1) {
      if (res.code === -15) {
        openMemberUpgradeDialog({ message: res.msg })
      }
      throw new Error(res.msg || '素材入库失败')
    }

    // Step 5: 成功清理
    removeUploadTask(taskId)
    triggerAssetRefresh(assetType)

    return {
      taskId,
      url: res.data?.material?.url || uploadedUrl,
      name,
      type: assetType,
      size: res.data?.material?.size || file.size,
      width,
      height,
      duration,
      ext,
      mimeType: file.type,
      nodeId: options.nodeId,
      materialId: res.data!.material.material_id,
    }
  } catch (error) {
    removeUploadTask(taskId)
    throw error
  }
}
